import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Download } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { authApi } from '../services/api';
import { useAuthStore } from '../services/auth';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { setAuth } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await authApi.login(email, password);
      setAuth(response);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 py-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="HumanVu" className="h-16 w-auto mx-auto" />
          <span className="block mt-2 text-2xl font-bold text-gray-900 dark:text-white">HumanVu</span>
          <p className="mt-1 text-gray-500 dark:text-slate-400">Sign in to your account</p>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            )}

            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />

            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                className="absolute right-3 top-8 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>

            <Button type="submit" className="w-full" isLoading={isLoading}>
              Sign In
            </Button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200 dark:border-slate-700" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white dark:bg-slate-800 px-2 text-gray-400 dark:text-slate-500">or</span></div>
            </div>

            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={() => {
                setAuth({
                  access_token: 'demo-token',
                  refresh_token: 'demo-refresh',
                  user: {
                    id: 'demo-user',
                    email: 'demo@dominate.io',
                    role: 'admin',
                    organization_id: 'demo-org',
                    created_at: new Date().toISOString(),
                  },
                });
                navigate('/');
              }}
            >
              Demo Mode (No Backend)
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500 dark:text-slate-400">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary-500 dark:text-primary-400 hover:text-primary-700">
              Sign up
            </Link>
          </p>
        </Card>

        <div className="mt-6 text-center">
          <Link
            to="/downloads"
            className="inline-flex items-center text-sm text-gray-500 dark:text-slate-400 hover:text-primary-500 dark:hover:text-primary-400"
          >
            <Download className="w-4 h-4 mr-2" />
            Download the client application
          </Link>
        </div>
      </div>
    </div>
  );
}
